import React, { useState, useEffect, useRef } from 'react'
import posthog from 'posthog-js'
import { Link, useLocation } from 'react-router-dom'
import { isMobileDevice, hasMobileUserAgent, isIOS } from '../utils/device.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { api } from '../services/api.js'
import './MobileBlocker.css'
import Logo from '/icons/autoinvent-full-logo.svg'

/** The iOS app. `ascAppId` in mobile/eas.json is the same number. */
const APP_STORE_URL = 'https://apps.apple.com/app/id6801010063'

const MobileBlocker = ({ children }) => {
  /* Decided on the first render, not after it.
   *
   * Starting at false meant one paint of the children before the wall replaced them. That was
   * invisible while every walled route also existed behind the wall — but the paywall now
   * routes here on a phone and has no route in this branch, so the flash would have been a
   * blank screen rather than a glimpse of the dashboard. */
  const [isMobile, setIsMobile] = useState(() => isMobileDevice())
  const [emailState, setEmailState] = useState('idle')
  const [sentTo, setSentTo] = useState(null)
  const location = useLocation()
  const reported = useRef(false)
  const { user } = useAuth()

  useEffect(() => {
    // The threshold and the user-agent test live in utils/device.js, so the record written
    // at signup cannot disagree with what the visitor was actually shown.
    const checkMobile = () => setIsMobile(isMobileDevice())

    // Check on mount
    checkMobile()

    // Check on resize
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  /**
   * Say when this screen is shown.
   *
   * Until now it was silent, and silence is why nobody could ever answer "how many people
   * did we turn away on a phone?" — the wall wrote nothing down, no field, no event, no
   * request. Reconstructing that cohort after the fact turned out to be impossible, and the
   * recovery campaign had to fall back on "signed up and did nothing" as a proxy for it.
   *
   * Fired once per mount rather than on every resize, so dragging a desktop window across
   * the threshold does not spray events. Whether the person is identified is already carried
   * by PostHog itself (utils/analytics.js sets the uid and email on sign-in), so for anyone
   * with an account this is a directly addressable cohort — the thing that could not be done
   * this time.
   */
  useEffect(() => {
    if (!isMobile || reported.current) return
    reported.current = true
    try {
      posthog.capture('mobile_blocked', {
        blocked_path: location.pathname,
        viewport_width: window.innerWidth,
        // Which test caught them. A phone and a narrow desktop window are different
        // problems, and only one of them is worth emailing about.
        matched_user_agent: hasMobileUserAgent(),
      })
    } catch {
      // Analytics must never take a page down.
    }
  }, [isMobile, location.pathname])

  /*
   * A phone and a narrow desktop window are different problems and were being given the same
   * answer. Someone who has dragged their browser narrow is already ON a computer, so telling
   * them to find one reads as broken; someone on an iPhone cannot widen anything, so telling
   * them to try is the sentence that ends their visit. Only the user-agent test can tell the
   * two apart -- the viewport cannot -- which is the same reason the analytics event carries
   * it.
   */
  const onRealPhone = hasMobileUserAgent()

  /**
   * Send the way back to the address on the account.
   *
   * The server chooses the recipient, so this cannot mail anybody else. The path travels so
   * the link lands on the screen they were reaching for -- 224 of 246 blocks happen on
   * /dashboard, but the ones on /abandoned-patents are the people the wall costs most.
   */
  const emailMeALink = async () => {
    if (emailState === 'sending') return
    setEmailState('sending')
    try {
      const res = await api.sendDesktopLink(location.pathname + location.search)
      setSentTo(res?.data?.sentTo || user?.email || null)
      setEmailState('sent')
      try {
        posthog.capture('desktop_link_emailed', { blocked_path: location.pathname })
      } catch {
        // Analytics must never take a screen down.
      }
    } catch {
      setEmailState('error')
    }
  }

  if (isMobile) {
    return (
      <div className="mobile-blocker">
        <div className="mobile-blocker-content">
          <img src={Logo} alt="AutoInvent" className="mobile-blocker-logo" />

          <div className="mobile-blocker-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="4" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 22H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M12 18V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>

          <div className="mobile-blocker-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="4" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 22H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M12 18V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span>Desktop Required</span>
          </div>

          <h1 className="mobile-blocker-title">AutoInvent works best on desktop</h1>

          {!onRealPhone && (
            <>
              <p className="mobile-blocker-description">
                Our patent tools need a larger screen.<br />
                Widen your browser window to continue.
              </p>
              <p className="mobile-blocker-footer">This screen disappears as soon as there's room</p>
            </>
          )}

          {onRealPhone && (
            <>
              <p className="mobile-blocker-description">
                Our patent tools need a bigger screen.
              </p>

              {isIOS() && (
                <a className="mobile-blocker-btn mobile-blocker-btn-primary" href={APP_STORE_URL}>
                  Get the iPhone app
                </a>
              )}

              {/*
                The Android answer, and the only one there is: there is no Android app, so the
                way forward is a computer and the way to reach one is their inbox. Requires an
                account, because the server sends to the address on it rather than to anything
                typed here -- and 78% of the people who see this screen are already signed in.
              */}
              {user && emailState !== 'sent' && (
                <button
                  type="button"
                  className={`mobile-blocker-btn ${isIOS() ? 'mobile-blocker-btn-secondary' : 'mobile-blocker-btn-primary'}`}
                  onClick={emailMeALink}
                  disabled={emailState === 'sending'}
                >
                  {emailState === 'sending' ? 'Sending…' : 'Email me a link for my computer'}
                </button>
              )}

              {/*
                Signed out and on a phone, which happens because this wall short-circuits
                BEFORE ProtectedRoute -- a bookmark or the back button lands here rather than
                on /login. There is no account to mail, so the only honest next step is the
                sign-in page, and that page is exempt from this wall so it will actually open.
              */}
              {!user && (
                <Link className="mobile-blocker-btn mobile-blocker-btn-secondary" to="/login">
                  Sign in to email yourself a link
                </Link>
              )}

              {emailState === 'sent' && (
                <p className="mobile-blocker-sent">
                  Sent to <strong>{sentTo}</strong>. Open it on your computer.
                </p>
              )}

              {emailState === 'error' && (
                <p className="mobile-blocker-error">
                  That didn&apos;t send. Try again in a moment.
                </p>
              )}

              <p className="mobile-blocker-footer">
                Your account and everything in it is waiting. Nothing is lost.
              </p>
            </>
          )}
        </div>
      </div>
    )
  }

  return children
}

export default MobileBlocker
